// src/components/onboarding/Welcome.js
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { mockRepositories } from '../../utils/mockData'
import Button from '../common/Button'
import { Switch } from '@headlessui/react'

export default function Welcome() {
  const [repositories, setRepositories] = useState(mockRepositories)
  const [allEnabled, setAllEnabled] = useState(true)
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    // Auto-enable all repositories by default
    setRepositories(repos => 
      repos.map(repo => ({ ...repo, isActive: true }))
    )
  }, [])

  const toggleRepository = (id) => {
    setRepositories(repos =>
      repos.map(repo =>
        repo.id === id ? { ...repo, isActive: !repo.isActive } : repo
      )
    )
  }

  const toggleAllRepositories = () => {
    const newState = !allEnabled
    setAllEnabled(newState)
    setRepositories(repos =>
      repos.map(repo => ({ ...repo, isActive: newState }))
    )
  }

  const handleComplete = () => {
    // Save settings and navigate to dashboard
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to EchoGuard!</h1>
          <p className="mt-2 text-sm text-gray-600">
            Let's get your repositories set up with automation
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Quick Configuration</h3>
                <p className="text-sm text-gray-600">
                  Only one step to start automating your Issue management. You can fine-tune settings for each repository later.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-medium text-gray-900">
                  Enable EchoGuard for all repositories with default configuration
                </h4>
                <Switch
                  checked={allEnabled}
                  onChange={toggleAllRepositories}
                  className={`${
                    allEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      allEnabled ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
              
              <div className="text-sm text-gray-600 mb-4">
                <p className="font-medium mb-2">Default configuration includes:</p>
                <ul className="space-y-1 ml-4">
                  <li>• Enable Spam Check</li>
                  <li>• Enable automatic title generation for new Issues</li>
                  <li>• Enable similarity detection for new Issues</li>
                  <li>• Apply to "Issues" only, not "Discussions"</li>
                </ul>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-base font-medium text-gray-900">Your Repositories</h4>
              {repositories.map((repo) => (
                <div key={repo.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{repo.fullName}</p>
                      <p className="text-xs text-gray-500">Last activity: {repo.lastActivity}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-3 text-sm text-gray-700">
                      {repo.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                      checked={repo.isActive}
                      onChange={() => toggleRepository(repo.id)}
                      className={`${
                        repo.isActive ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          repo.isActive ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Button
                onClick={handleComplete}
                className="w-full"
                size="lg"
              >
                Complete Setup
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

